import { Spinner } from "components/Loading";
import React, { HTMLProps, PropsWithChildren } from "react";
import Twemoji from "react-twemoji";
import settings from "settings";
import { PuzzleType } from "sockapi/puzzle-data";
import { useNavStore } from "stores/NavStore";
import {
  useAccessiblePuzzles,
  usePuzzleDisplayName,
} from "stores/ScopeStateStore";
import { Routes, RouteName, RouteSpec, resolveRoute } from "components/urls";
import { useEvent } from "client-util/useEvent";

type BaseLinkProps = {
  disabled?: boolean;
  button?: true;
};

function BaseLink({
  children,
  disabled,
  button,
  ...otherProps
}: PropsWithChildren<BaseLinkProps> & HTMLProps<HTMLAnchorElement>) {
  if (!button) return <a {...otherProps}>{children}</a>;
  return (
    <a {...otherProps}>
      <button disabled={disabled} style={{ cursor: "unset" }} tabIndex={-1}>
        {children}
      </button>
    </a>
  );
}

type LinkProps<P> = PropsWithChildren<P & BaseLinkProps> &
  Omit<HTMLProps<HTMLAnchorElement>, "href">;

export const useOnClickRoutedLink = () => {
  const setRedirectCommand = useNavStore((state) => state.setRedirectCommand);
  return useEvent((e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    // do not intercept if someone is opening new tabs
    if (e.ctrlKey || e.shiftKey) return;
    setRedirectCommand({ path: href, isManual: true });
    e.preventDefault();
  });
};

export function Link<R extends RouteName>(props: LinkProps<RouteSpec<R>>) {
  const { children, route, params, disabled, ...otherProps } = props;
  const hasRouter = useNavStore((state) => state.hasRouter);
  const href = resolveRoute(props);
  const onClickRoutedLink = useOnClickRoutedLink();
  if (disabled)
    return (
      <BaseLink disabled {...otherProps}>
        {children}
      </BaseLink>
    );
  if (Routes[route].element === undefined)
    return (
      <BaseLink
        href={settings.djangoBaseUrl + href.replace(/^\//, "")}
        {...otherProps}
      >
        {children}
      </BaseLink>
    );
  if (!hasRouter || "target" in otherProps)
    return (
      <BaseLink href={href} {...otherProps}>
        {children}
      </BaseLink>
    );
  return (
    <BaseLink
      href={href}
      onClick={(e) => onClickRoutedLink(e, href)}
      {...otherProps}
    >
      {children}
    </BaseLink>
  );
}

type PuzzleLinkSubpage = "piece" | "puzzle" | "solution" | "hints" | "stats";

// A PuzzleLink is a link to a puzzle.
type PuzzleLinkProps = {
  puzName: string;
  subpage?: PuzzleLinkSubpage;
  solved?: boolean;
};

export function PuzzleLink({
  children,
  puzName,
  subpage,
  solved = false,
  ...otherProps
}: LinkProps<PuzzleLinkProps>) {
  // We inline this logic here so that we can treat missing scope states
  // separately from missing puzzle data.
  const puzzles = useAccessiblePuzzles();
  const displayName = usePuzzleDisplayName(puzName);
  if (puzzles === null) return <Spinner inline />;
  const puzData = puzzles[puzName] ?? null;
  if (puzData === null)
    return (
      <BaseLink disabled {...otherProps}>
        {children ?? "???"}
      </BaseLink>
    );
  const { slug } = puzData;
  const resolvedSubpage =
    subpage ??
    ((): PuzzleLinkSubpage => {
      switch (puzData.type) {
        case PuzzleType.PIECE:
          return "piece";
        case PuzzleType.PUZZLE:
        case PuzzleType.STORY:
          return "puzzle";
      }
    })();
  return (
    <Link route={resolvedSubpage} params={{ slug }} {...otherProps}>
      {solved ? <Twemoji tag="span">✅ </Twemoji> : null}
      {children ?? <Twemoji tag="span">{displayName ?? "???"}</Twemoji>}
    </Link>
  );
}
